import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'

dayjs.extend(customParseFormat)

export type ISODate = string // 'YYYY-MM-DD'

export type ParsedRange =
  | { kind: 'day'; from: ISODate; to: ISODate }
  | { kind: 'month'; from: ISODate; to: ISODate }
  | { kind: 'year'; from: ISODate; to: ISODate }

export type ParseResult = { ok: true; value: ParsedRange } | { ok: false; reason: 'empty' | 'invalid' }

export type ParseDateInputOptions = { allowPartial: boolean }

export function parseDateInput(raw: string, opts: ParseDateInputOptions): ParseResult {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return { ok: false, reason: 'empty' }

  const tokens = trimmed.split(/[.\-\s]+/).filter(t => t.length > 0)
  if (tokens.length < 1 || tokens.length > 3) return { ok: false, reason: 'invalid' }
  if (tokens.some(t => !/^\d+$/.test(t))) return { ok: false, reason: 'invalid' }

  if (tokens.length === 3) {
    const [d, m, y] = tokens.map(Number)
    return parseDay(d, m, expandYear(y))
  }

  if (!opts.allowPartial) return { ok: false, reason: 'invalid' }

  if (tokens.length === 2) {
    const [m, y] = tokens.map(Number)
    return parseMonth(m, expandYear(y))
  }

  const [y] = tokens.map(Number)
  return parseYear(expandYear(y))
}

function expandYear(yy: number): number {
  if (yy >= 100) return yy
  const cutoff = new Date().getFullYear() % 100
  return yy <= cutoff ? 2000 + yy : 1900 + yy
}

function parseDay(d: number, m: number, y: number): ParseResult {
  const candidate = dayjs(`${pad(y, 4)}-${pad(m, 2)}-${pad(d, 2)}`, 'YYYY-MM-DD', true)
  if (!candidate.isValid()) return { ok: false, reason: 'invalid' }
  const iso = candidate.format('YYYY-MM-DD')
  return { ok: true, value: { kind: 'day', from: iso, to: iso } }
}

function parseMonth(m: number, y: number): ParseResult {
  const first = dayjs(`${pad(y, 4)}-${pad(m, 2)}-01`, 'YYYY-MM-DD', true)
  if (!first.isValid()) return { ok: false, reason: 'invalid' }
  return {
    ok: true,
    value: {
      kind: 'month',
      from: first.format('YYYY-MM-DD'),
      to: first.endOf('month').format('YYYY-MM-DD'),
    },
  }
}

function parseYear(y: number): ParseResult {
  if (y < 1 || y > 9999) return { ok: false, reason: 'invalid' }
  return {
    ok: true,
    value: {
      kind: 'year',
      from: `${pad(y, 4)}-01-01`,
      to: `${pad(y, 4)}-12-31`,
    },
  }
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0')
}
